import { useState, useCallback } from 'react';
import { Mui } from '@osu/react-ui';
import { useNavigate } from 'react-router-dom';
import { useAdmitScholarsFromAlternateMutation } from '../../services/graphql/api';
import useRoleAuthentication from '../../Authentication/hooks/useRoleAuthentication';
import useChipSubProgram from '../../util/scholars/useChipSubProgram';

// has552 — Admit an OPT-IN (ALTERNATE_PENDING) applicant into a Program Manager's own program from the
// applicant detail page. Opt-in applicants exhausted their ranked preferences (no offers) and opted in, so
// they sit on the Opt-In List with NO active queue entry — the normal recommend flow (which requires an
// IN_REVIEW entry) can't act on them. admitScholarsFromAlternate is the purpose-built path: it FCFS-claims
// the applicant off the opt-in list, seeds a RECOMMENDED placement for the PM's program, and starts the
// approval workflow so it lands on "Recommendations to Approve" for admin approval (term resolved server-side).
//
// PM-only by design (has552): admins stay read-only here (they have no single "own" program and approve in
// rec-to-approve). There is intentionally NO deny — a PM who won't admit simply leaves the applicant on the
// Opt-In List for another PM to consider.

// Detects the backend AlternateAdmitConflict (FCFS loser): another PM recommended this applicant first, so
// they are no longer ALTERNATE_PENDING. The transformed GraphQL error exposes the ScholarsError code as
// `errorType` on each entry in `errors`; fall back to a message-substring match for robustness.
function isNoLongerAvailable(err) {
    const CODE = 'AlternateAdmitConflict';
    if (Array.isArray(err?.errors) && err.errors.some((e) => e?.errorType === CODE)) { return true; }
    const message = typeof err?.message === 'string' ? err.message : '';
    return message.includes(CODE) || message.toLowerCase().includes('no longer available');
}

export default function ScholarsOptInAdmitPanel({ applicantEmployeeId }) {
    const { isScholarsProgramManager } = useRoleAuthentication();
    const navigate = useNavigate();
    // The PM's own program (identity-based; exactly one by business rule). Null for admins/non-owners.
    const ownProgram = useChipSubProgram();
    const [admit, { isLoading }] = useAdmitScholarsFromAlternateMutation();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [result, setResult] = useState('');

    const subProgramId = ownProgram?.subProgramId;
    const programLabel = ownProgram?.label ?? subProgramId;

    const onConfirm = useCallback(async () => {
        setResult('');
        try {
            await admit({ applicantEmployeeId, subProgramId }).unwrap();
            setDialogOpen(false);
            // Return to the PM's program queue after admitting (mirrors ScholarsDecisionPanel).
            navigate(`/scholars/queue/${subProgramId}`);
        } catch (err) {
            setDialogOpen(false);
            // has552 (#7) — FCFS: another PM already recommended this applicant, so the backend's guarded
            // claim failed with AlternateAdmitConflict. Surface a clear "no longer available" message so the
            // PM knows why, rather than the generic capacity/closed error. The transformed GraphQL error
            // carries the backend ScholarsError code as errorType; also match the message as a fallback.
            if (isNoLongerAvailable(err)) {
                setResult('This applicant is no longer available — another program manager has already recommended them. They have been removed from the Opt-In List.');
            } else {
                setResult('Unable to admit this applicant. Your program may be full or closed. Please try again.');
            }
        }
    }, [admit, applicantEmployeeId, subProgramId, navigate]);

    // Only Program Managers with a resolvable own program get the action. Admins / unresolved → read-only
    // "opted in" note so the section isn't empty.
    if (!isScholarsProgramManager || !subProgramId) {
        return (
            <Mui.Alert severity="info" sx={{ mt: 1 }} data-testid="scholars-optin-readonly">
                This applicant received no offers from their preferences and opted in to the Opt-In List.
            </Mui.Alert>
        );
    }

    return (
        <Mui.Box sx={{ mt: 1 }} data-testid="scholars-optin-admit-panel">
            <Mui.Typography sx={{ mb: 1 }}>
                This applicant is on the Opt-In List (no offers from their preferences).
            </Mui.Typography>
            <Mui.Button
                variant="contained"
                color="primary"
                onClick={() => setDialogOpen(true)}
                disabled={isLoading}
                data-testid="scholars-optin-admit"
            >
                {isLoading ? 'Admitting…' : `Admit into ${programLabel}`}
            </Mui.Button>

            <Mui.Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} aria-labelledby="scholars-optin-admit-dialog-title">
                <Mui.DialogTitle id="scholars-optin-admit-dialog-title">Admit from Opt-In List</Mui.DialogTitle>
                <Mui.DialogContent>
                    <Mui.Typography>
                        You are admitting this applicant into <strong>{programLabel}</strong>. This sends them to the
                        Recommendations to Approve report for a Scholars admin to approve.
                    </Mui.Typography>
                </Mui.DialogContent>
                <Mui.DialogActions>
                    <Mui.Button onClick={() => setDialogOpen(false)} disabled={isLoading}>Cancel</Mui.Button>
                    <Mui.Button onClick={onConfirm} variant="contained" disabled={isLoading} data-testid="scholars-optin-admit-confirm">
                        {isLoading ? 'Admitting…' : 'Admit'}
                    </Mui.Button>
                </Mui.DialogActions>
            </Mui.Dialog>

            <Mui.Box aria-live="polite" role="status" sx={{ mt: 1 }}>{result}</Mui.Box>
        </Mui.Box>
    );
}
